/**
 * Module Definitions — 10 learning modules
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/data/modules.ts in R24.
 * 10 modules: GET, REK, MET, TIJD, GELD, SWW, SPR, TAA, STU, TOPO.
 */

import { ModuleData, ModuleId } from './types'

export const MODULES: ModuleData[] = [
  {
    id: 'Metriek',
    title: 'Meten & Omrekenen',
    description:
      'Lengtematen, gewicht, inhoud, oppervlakte en omtrek omrekenen en berekenen.',
    icon: 'Ruler',
    color: 'text-blue-500',
  },
  {
    id: 'Getalbegrip',
    title: 'Getallen',
    description:
      'Grote getallen, breuken, procenten, verhoudingen en kommagetallen.',
    icon: 'Calculator',
    color: 'text-indigo-500',
  },
  {
    id: 'Basisrekenen',
    title: 'Basisrekenen',
    description:
      'Tafels, cijferend rekenen, handig rekenen, contextopgaven en gemengde bewerkingen.',
    icon: 'Plus',
    color: 'text-orange-500',
  },
  {
    id: 'Tijd',
    title: 'Tijd & Klok',
    description: "Klokkijken, tijdsduur berekenen, schema's lezen en snelheid.",
    icon: 'Clock',
    color: 'text-sky-500',
  },
  {
    id: 'Geld',
    title: 'Geld & Financieel',
    description: 'Geld tellen, wisselgeld, korting, BTW en budgetteren.',
    icon: 'Wallet',
    color: 'text-yellow-500',
  },
  {
    id: 'SpellingWW',
    title: 'Werkwoordspelling',
    description:
      "Stam bepalen, 't Kofschip, verleden tijd en voltooid deelwoord.",
    icon: 'PenTool',
    color: 'text-fuchsia-500',
  },
  {
    id: 'SpellingRegel',
    title: 'Spelling & Grammatica',
    description: 'Spellingregels, weetwoorden, leenwoorden en zinsontleding.',
    icon: 'BookOpen',
    color: 'text-amber-500',
  },
  {
    id: 'Taalbegrip',
    title: 'Taalbegrip & Lezen',
    description:
      'Begrijpend lezen, woordenschat, verwijswoorden en signaalwoorden.',
    icon: 'MessageSquare',
    color: 'text-purple-500',
  },
  {
    id: 'Studievaardig',
    title: 'Studievaardigheden',
    description:
      "Informatie opzoeken, kaartlezen, schema's en tabellen interpreteren.",
    icon: 'Search',
    color: 'text-teal-500',
  },
  {
    id: 'Topo',
    title: 'Topografie',
    description: 'Topografie van Nederland, Europa en de wereld.',
    icon: 'Globe',
    color: 'text-cyan-500',
  },
]

/** Short display names for module IDs — used in compact UI like heatmaps */
const MODULE_SHORT_NAMES: Record<string, string> = {
  Metriek: 'Meten',
  Getalbegrip: 'Get.',
  Basisrekenen: 'Reken',
  Tijd: 'Tijd',
  Geld: 'Geld',
  SpellingWW: 'WW',
  SpellingRegel: 'Spell.',
  Taalbegrip: 'Taal',
  Studievaardig: 'Studie',
  Topo: 'Topo',
}

/** Get a short display name for a module ID (e.g. 'Metriek' → 'Meten') */
export function getModuleShortName(moduleId: string): string {
  return MODULE_SHORT_NAMES[moduleId] ?? moduleId.substring(0, 4)
}

// ============================================================
// Module Code Mappings — SINGLE SOURCE OF TRUTH
// All short-code ↔ ModuleId lookups MUST use these.
// Do NOT create local copies elsewhere.
// ============================================================

/** Canonical mapping: ModuleId → short code (e.g. 'Getalbegrip' → 'GET') */
export const MODULE_ID_TO_CODE: Record<ModuleId, string> = {
  Getalbegrip: 'GET',
  Basisrekenen: 'REK',
  Metriek: 'MET',
  Tijd: 'TIJD',
  Geld: 'GELD',
  SpellingWW: 'SWW',
  SpellingRegel: 'SPR',
  Taalbegrip: 'TAA',
  Studievaardig: 'STU',
  Topo: 'TOPO',
}

/** Reverse lookup: short code → ModuleId (e.g. 'GET' → 'Getalbegrip') */
export const MODULE_CODE_TO_ID: Record<string, ModuleId> = Object.fromEntries(
  Object.entries(MODULE_ID_TO_CODE).map(([id, code]) => [code, id as ModuleId]),
) as Record<string, ModuleId>
